"use client";

import type { LucideIcon } from "lucide-react";
import { HelpCircle, Settings } from "lucide-react";
import type { ComponentType } from "react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "../b37/sidebar";
import type { Organization } from "./org-switcher";
import { OrgSwitcher } from "./org-switcher";

export interface NavItem {
  href: string;
  icon: LucideIcon;
  isActive?: boolean;
  title: string;
}

export interface NavGroup {
  items: NavItem[];
  label?: string;
}

export interface AppSidebarProps {
  appIcon?: LucideIcon | ComponentType;
  appName: string;
  collapsible?: "offcanvas" | "icon" | "none";
  currentOrg?: Organization;
  navGroups: NavGroup[];
  /** Called when the Help footer item is clicked */
  onHelpClick?: () => void;
  onOrgChange?: (org: Organization) => void;
  organizations?: Organization[];
  /** href for the org settings page — passed to OrgSwitcher; shows gear for team_manager and account_manager roles */
  orgSettingsHref?: string;
  /** href for the app settings page. Defaults to /settings */
  settingsHref?: string;
  showOrgSwitcher?: boolean;
  variant?: "sidebar" | "floating" | "inset";
}

export function AppSidebar({
  appName,
  appIcon: AppIcon,
  navGroups,
  organizations = [],
  currentOrg,
  onOrgChange,
  showOrgSwitcher = false,
  collapsible = "offcanvas",
  variant = "sidebar",
  settingsHref = "/settings",
  orgSettingsHref,
  onHelpClick,
}: AppSidebarProps) {
  return (
    <Sidebar collapsible={collapsible} variant={variant}>
      <SidebarHeader className="z-10 p-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              className="group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-0 group-data-[collapsible=icon]:!overflow-visible data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              // 2. !size-8 keeps it square (fixing the hover shade)
              // 3. !p-0 ensures the icon is centered in that square
              // 4. !overflow-visible keeps the shadow
              size="lg"
            >
              <a href="/">
                {AppIcon ? (
                  <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-white text-foreground shadow-sm">
                    <AppIcon className="size-4" />
                  </div>
                ) : null}
                <div className="flex flex-col gap-0.5 leading-none group-data-[collapsible=icon]:hidden">
                  <span className="font-medium">{appName}</span>
                </div>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        {showOrgSwitcher && organizations.length > 0 ? (
          <OrgSwitcher
            currentOrg={currentOrg}
            onOrgChange={onOrgChange}
            organizations={organizations}
            orgSettingsHref={orgSettingsHref}
          />
        ) : null}
      </SidebarHeader>
      <SidebarContent>
        {navGroups.map((group) => (
          <SidebarGroup
            key={group.label ?? group.items.map((i) => i.href).join("|")}
          >
            {group.label ? (
              <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
            ) : null}
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      isActive={item.isActive}
                      tooltip={item.title}
                    >
                      <a href={item.href}>
                        <item.icon />
                        <span>{item.title}</span>
                      </a>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              className="text-muted-foreground"
              tooltip="Settings"
            >
              <a href={settingsHref}>
                <Settings />
                <span>Settings</span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
          {/* Only render Help when a handler is provided — otherwise the
              button is a no-op (e.g. Pulse, Habitat, Circle don't use it). */}
          {onHelpClick ? (
            <SidebarMenuItem>
              <SidebarMenuButton
                className="text-muted-foreground"
                onClick={onHelpClick}
                tooltip="Help"
              >
                <HelpCircle />
                <span>Help</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ) : null}
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail className="z-50 will-change-[transform,opacity]" />
    </Sidebar>
  );
}
